import React, { useState, useRef, useCallback, useEffect } from "react";
import { metricsAPI, callObservationsAPI } from "../../services/api";
import { CheckCircle2, XCircle, Loader2, Info, Play, ChevronDown, BookOpen } from "lucide-react";

const CODE_EXAMPLES = [
  {
    name: "Silence > 5 Seconds",
    desc: "Check if any silence period exceeds 5 seconds",
    resultType: "boolean",
    code: `silence = context["silence"]
silences = silence.get("silences", [])
threshold_ms = 5000
long_silences = [s for s in silences if s.get("duration_ms", 0) > threshold_ms]

if len(long_silences) == 0:
    metric["result"] = True
    metric["explanation"] = "No silence periods exceeded 5 seconds"
    structured_output["name"] = "silence_compliance"
    structured_output["value"] = "pass"
    structured_output["classification"] = "meets_expectations"
else:
    worst_ms = max(s["duration_ms"] for s in long_silences)
    metric["result"] = False
    metric["explanation"] = str(len(long_silences)) + " silence(s) exceeded 5s (worst: " + str(worst_ms/1000) + "s)"
    structured_output["name"] = "silence_compliance"
    structured_output["value"] = "fail"
    structured_output["classification"] = "requires_attention"`,
  },
  {
    name: "Average Latency Check",
    desc: "Fail if average response latency exceeds thresholds",
    resultType: "boolean",
    code: `lat = context["latency"]
avg = lat.get("avg_ms", 0)

if avg < 1000:
    metric["result"] = True
    metric["explanation"] = "Average latency " + str(avg) + "ms is under 1 second"
    structured_output["name"] = "latency_check"
    structured_output["value"] = "pass"
    structured_output["classification"] = "meets_expectations"
elif avg < 2000:
    metric["result"] = False
    metric["explanation"] = "Average latency " + str(avg) + "ms is between 1-2 seconds"
    structured_output["name"] = "latency_check"
    structured_output["value"] = "warning"
    structured_output["classification"] = "requires_attention"
else:
    metric["result"] = False
    metric["explanation"] = "Average latency " + str(avg) + "ms exceeds 2 seconds"
    structured_output["name"] = "latency_check"
    structured_output["value"] = "fail"
    structured_output["classification"] = "goal_missed"`,
  },
  {
    name: "Agent Greeting Check",
    desc: "Verify the agent greets the caller",
    resultType: "boolean",
    code: `transcript = context["transcript_json"]
agent_roles = ["assistant", "ai assistant", "bot", "agent"]
first_agent = ""
for t in transcript:
    if t.get("role", "").lower() in agent_roles:
        first_agent = t.get("content", "")
        break

greetings = ["hello", "hi", "good morning", "good afternoon", "welcome", "thank you for calling"]
greeted = any(g in first_agent.lower() for g in greetings)

metric["result"] = greeted
metric["explanation"] = "Agent opened with: " + first_agent[:80] if first_agent else "No agent message found"
structured_output["name"] = "greeting_check"
structured_output["value"] = "pass" if greeted else "fail"
structured_output["classification"] = "meets_expectations" if greeted else "requires_attention"`,
  },
  {
    name: "Agent Closing Check",
    desc: "Verify the agent properly closes the conversation",
    resultType: "boolean",
    code: `transcript = context["transcript_json"]
agent_roles = ["assistant", "ai assistant", "bot", "agent"]
agent_msgs = [t["content"] for t in transcript if t.get("role", "").lower() in agent_roles]
last_agent = agent_msgs[-1].lower() if agent_msgs else ""

closings = ["goodbye", "have a great day", "thank you", "thanks for calling", "is there anything else", "take care"]
closed = any(c in last_agent for c in closings)

metric["result"] = closed
metric["explanation"] = "Agent closed with: " + (agent_msgs[-1][:80] if agent_msgs else "No agent message")
structured_output["name"] = "closing_check"
structured_output["value"] = "pass" if closed else "fail"
structured_output["classification"] = "meets_expectations" if closed else "requires_attention"`,
  },
  {
    name: "Speech Speed Assessment",
    desc: "Rate speech speed on a 1-5 scale",
    resultType: "rating",
    code: `w = context["wpm"]
avg_wpm = w.get("avg_wpm", 0)

if 120 <= avg_wpm <= 180:
    metric["result"] = 5
    metric["explanation"] = "Speech speed " + str(avg_wpm) + " WPM is in ideal range (120-180)"
    structured_output["name"] = "speech_speed"
    structured_output["value"] = "ideal"
    structured_output["classification"] = "meets_expectations"
elif 100 <= avg_wpm < 120 or 180 < avg_wpm <= 200:
    metric["result"] = 3
    metric["explanation"] = "Speech speed " + str(avg_wpm) + " WPM is slightly outside ideal range"
    structured_output["name"] = "speech_speed"
    structured_output["value"] = "acceptable"
    structured_output["classification"] = "requires_attention"
else:
    metric["result"] = 1
    metric["explanation"] = "Speech speed " + str(avg_wpm) + " WPM is too slow or too fast"
    structured_output["name"] = "speech_speed"
    structured_output["value"] = "poor"
    structured_output["classification"] = "goal_missed"`,
  },
  {
    name: "Interruption Count",
    desc: "Count total interruptions and classify severity",
    resultType: "numeric",
    code: `ints = context["interruptions"]
user_count = ints.get("user", {}).get("count", 0)
ai_count = ints.get("ai", {}).get("count", 0)
total = user_count + ai_count

metric["result"] = total
metric["explanation"] = "Total " + str(total) + " interruptions (user: " + str(user_count) + ", AI: " + str(ai_count) + ")"
structured_output["name"] = "interruption_level"
if total <= 3:
    structured_output["value"] = "low"
    structured_output["classification"] = "meets_expectations"
elif total <= 8:
    structured_output["value"] = "moderate"
    structured_output["classification"] = "requires_attention"
else:
    structured_output["value"] = "high"
    structured_output["classification"] = "goal_missed"`,
  },
  {
    name: "Voice Tone Quality",
    desc: "Check voice clarity and tone scores (1-5)",
    resultType: "rating",
    code: `vt = context["voice_tone"]
score = vt.get("overall_score", 0)
clarity = vt.get("clarity_score", 0)
tone = vt.get("tone_score", 0)

metric["result"] = round(score)
metric["explanation"] = "Voice tone score: " + str(score) + "/5 (clarity: " + str(clarity) + ", tone: " + str(tone) + ")"
structured_output["name"] = "voice_quality"
if score >= 4:
    structured_output["value"] = "excellent"
    structured_output["classification"] = "exceeds_expectations"
elif score >= 3:
    structured_output["value"] = "acceptable"
    structured_output["classification"] = "meets_expectations"
else:
    structured_output["value"] = "poor"
    structured_output["classification"] = "requires_attention"`,
  },
  {
    name: "Call Duration Check",
    desc: "Ensure call is within 30s-10min range",
    resultType: "boolean",
    code: `dur = context["call_duration"] or 0

if dur < 30:
    metric["result"] = False
    metric["explanation"] = "Call too short at " + str(dur) + "s"
    structured_output["name"] = "duration_check"
    structured_output["value"] = "too_short"
    structured_output["classification"] = "requires_attention"
elif dur > 600:
    metric["result"] = False
    metric["explanation"] = "Call too long at " + str(dur) + "s"
    structured_output["name"] = "duration_check"
    structured_output["value"] = "too_long"
    structured_output["classification"] = "requires_attention"
else:
    metric["result"] = True
    metric["explanation"] = "Call duration " + str(dur) + "s is within acceptable range"
    structured_output["name"] = "duration_check"
    structured_output["value"] = "acceptable"
    structured_output["classification"] = "meets_expectations"`,
  },
  {
    name: "Conversation Turn Count",
    desc: "Count turns and flag abnormal lengths",
    resultType: "numeric",
    code: `transcript = context["transcript_json"]
agent_roles = ["assistant", "ai assistant", "bot", "agent"]
user_roles = ["user", "human", "customer", "caller"]
total = len(transcript)
agent_turns = len([t for t in transcript if t.get("role", "").lower() in agent_roles])
user_turns = len([t for t in transcript if t.get("role", "").lower() in user_roles])

metric["result"] = total
metric["explanation"] = str(total) + " total turns (agent: " + str(agent_turns) + ", user: " + str(user_turns) + ")"
structured_output["name"] = "turn_count"
if total < 4:
    structured_output["value"] = "too_short"
    structured_output["classification"] = "requires_attention"
elif total > 50:
    structured_output["value"] = "too_long"
    structured_output["classification"] = "requires_attention"
else:
    structured_output["value"] = "normal"
    structured_output["classification"] = "meets_expectations"`,
  },
  {
    name: "Call End Reason Check",
    desc: "Verify the call ended normally",
    resultType: "boolean",
    code: `reason = context.get("call_end_reason", "")
normal_reasons = ["user disconnected", "hangup", "completed", "end_turn", "natural_conclusion", "objective_met"]
is_normal = any(r in str(reason).lower() for r in normal_reasons) if reason else False

metric["result"] = is_normal
metric["explanation"] = "Call ended with: " + str(reason)
structured_output["name"] = "end_reason"
structured_output["value"] = "normal" if is_normal else "abnormal"
structured_output["classification"] = "meets_expectations" if is_normal else "requires_attention"`,
  },
  {
    name: "Agent Identifies Themselves",
    desc: "Check if agent introduces themselves in first 3 turns",
    resultType: "boolean",
    code: `transcript = context["transcript_json"]
agent_roles = ["assistant", "ai assistant", "bot", "agent"]
first_3 = [t["content"].lower() for t in transcript if t.get("role", "").lower() in agent_roles][:3]
combined = " ".join(first_3)

identified = any(p in combined for p in ["my name is", "this is", "i'm ", "i am "])

metric["result"] = identified
metric["explanation"] = "Agent introduced themselves" if identified else "Agent did not introduce themselves in first 3 turns"
structured_output["name"] = "agent_identification"
structured_output["value"] = "pass" if identified else "fail"
structured_output["classification"] = "meets_expectations" if identified else "requires_attention"`,
  },
  {
    name: "Metadata Presence Check",
    desc: "Verify that call metadata is attached",
    resultType: "boolean",
    code: `meta = context.get("metadata", {})
has_meta = len(meta) > 0

metric["result"] = has_meta
keys = list(meta.keys())
metric["explanation"] = "Metadata present: " + ", ".join(keys[:5]) if has_meta else "No metadata attached to this call"
structured_output["name"] = "metadata_check"
structured_output["value"] = "present" if has_meta else "missing"
structured_output["classification"] = "meets_expectations" if has_meta else "requires_attention"`,
  },
];

const CONTEXT_ATTRIBUTES = [
  { key: "transcript", desc: "Full conversation transcript as text", example: 'context["transcript"]' },
  { key: "transcript_json", desc: "Structured transcript with roles and timestamps", example: 'context["transcript_json"]' },
  { key: "call_duration", desc: "Call duration in seconds", example: 'context["call_duration"]' },
  { key: "call_end_reason", desc: "Why the call ended", example: 'context["call_end_reason"]' },
  { key: "call_timestamp", desc: "Actual call start time (UTC, ISO-8601); null if unknown", example: 'context["call_timestamp"]' },
  { key: "metadata", desc: "Custom metadata key-value pairs", example: 'context["metadata"]' },
  { key: "metrics_results", desc: "All computed metric results (name → result dict)", example: 'context["metrics_results"]' },
  { key: "latency", desc: "avg_ms, p95_ms, min_ms, max_ms, count, turns[]", example: 'context["latency"]' },
  { key: "silence", desc: "count, total_silence_ms, silences[]", example: 'context["silence"]' },
  { key: "interruptions", desc: "user: {count, events[]}, ai: {count, events[]}", example: 'context["interruptions"]' },
  { key: "wpm", desc: "avg_wpm, total_words, assessment", example: 'context["wpm"]' },
  { key: "voice_tone", desc: "overall_score, clarity_score, tone_score (1-5)", example: 'context["voice_tone"]' },
  { key: "voice_change", desc: "count, avg_similarity, assessment, events[]", example: 'context["voice_change"]' },
  { key: "transcription_accuracy", desc: "wer, mer (0-1 word error rate)", example: 'context["transcription_accuracy"]' },
  { key: "recording_metadata", desc: "Recording info (duration, channels, sample_rate)", example: 'context["recording_metadata"]' },
  { key: "agent_name", desc: "Name of the AI agent", example: 'context["agent_name"]' },
  { key: "agent_instructions", desc: "Agent's system prompt/instructions", example: 'context["agent_instructions"]' },
];

const PLACEHOLDER_CODE = `# ── Metric result (required) ──────────────────────────────
#
# metric["result"]       → value (must match your Result Type)
#                           boolean: True / False
#                           rating:  1-5
#                           enum:    one of your defined values
#                           numeric: any number
# metric["explanation"]  → short explanation string
#
# ── Structured output (optional) ──────────────────────────
#
# structured_output["name"]            → field name
# structured_output["value"]           → field value (e.g. "pass")
# structured_output["classification"]  → one of: meets_expectations,
#                                        exceeds_expectations,
#                                        requires_attention,
#                                        goal_achieved, goal_missed
#
# ── Example: Silence exceeds 5 seconds ───────────────────
#
silence = context["silence"]
silences = silence.get("silences", [])
threshold_ms = 5000  # 5 seconds
long_silences = [s for s in silences if s.get("duration_ms", 0) > threshold_ms]

if len(long_silences) == 0:
    metric["result"] = True
    metric["explanation"] = "No silence periods exceeded 5 seconds"
    structured_output["name"] = "silence_compliance"
    structured_output["value"] = "pass"
    structured_output["classification"] = "meets_expectations"
else:
    worst_ms = max(s["duration_ms"] for s in long_silences)
    metric["result"] = False
    metric["explanation"] = f"{len(long_silences)} silence(s) exceeded 5s (worst: {worst_ms/1000:.1f}s)"
    structured_output["name"] = "silence_compliance"
    structured_output["value"] = "fail"
    structured_output["classification"] = "requires_attention"
`;

const CodeMetricEditor = ({ code, onChange, clientId, agentId }) => {
  const textareaRef = useRef(null);
  const [validationState, setValidationState] = useState(null); // null | "validating" | {valid, errors}
  const [testState, setTestState] = useState(null); // null | "loading_calls" | "testing" | {result}
  const [testCalls, setTestCalls] = useState([]);
  const [showCallPicker, setShowCallPicker] = useState(false);
  const [callSearch, setCallSearch] = useState("");
  const callPickerRef = useRef(null);
  const [showExamples, setShowExamples] = useState(false);
  const examplesRef = useRef(null);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteFilter, setAutocompleteFilter] = useState("");
  const [autocompletePos, setAutocompletePos] = useState({ top: 0, left: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showReference, setShowReference] = useState(false);
  const autocompleteRef = useRef(null);

  const filteredAttrs = CONTEXT_ATTRIBUTES.filter((a) =>
    autocompleteFilter === "" || a.key.toLowerCase().includes(autocompleteFilter.toLowerCase())
  );

  // Validate code on demand
  const handleValidate = useCallback(async () => {
    if (!code?.trim() || !clientId) return;
    setValidationState("validating");
    try {
      const res = await metricsAPI.validateCode(clientId, code);
      setValidationState(res);
    } catch (e) {
      setValidationState({ valid: false, errors: [e.message || "Validation failed"] });
    }
  }, [code, clientId]);

  // Reset cached calls when agent changes
  useEffect(() => {
    setTestCalls([]);
    setShowCallPicker(false);
  }, [agentId]);

  // Load recent calls for testing
  const fetchCalls = useCallback(async () => {
    setTestState("loading_calls");
    try {
      const params = { page: 1, per_page: 20, analysis_status: "completed" };
      if (agentId) params.agent_id = agentId;
      const res = await callObservationsAPI.getCallObservations(clientId, params);
      const calls = res?.data?.call_observations || res?.data?.data || res?.data || [];
      setTestCalls(Array.isArray(calls) ? calls : []);
    } catch {
      setTestCalls([]);
    } finally {
      setTestState(null);
    }
  }, [clientId, agentId]);

  const handleOpenCallPicker = useCallback(async () => {
    if (testCalls.length > 0) {
      setShowCallPicker(!showCallPicker);
      return;
    }
    await fetchCalls();
    setShowCallPicker(true);
  }, [testCalls.length, showCallPicker, fetchCalls]);

  // Execute code against a selected call
  const handleTestWithCall = useCallback(async (callId) => {
    setShowCallPicker(false);
    setTestState("testing");
    try {
      const res = await metricsAPI.testCode(clientId, code, callId);
      setTestState({ ...res, _callId: callId });
    } catch (e) {
      setTestState({ error: e?.response?.data?.error || e.message || "Test failed" });
    }
  }, [code, clientId]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (callPickerRef.current && !callPickerRef.current.contains(e.target)) setShowCallPicker(false);
      if (examplesRef.current && !examplesRef.current.contains(e.target)) setShowExamples(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Handle keydown in textarea
  const handleKeyDown = (e) => {
    if (showAutocomplete) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filteredAttrs.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertAttribute(filteredAttrs[selectedIndex]);
      } else if (e.key === "Escape") {
        setShowAutocomplete(false);
      }
      return;
    }

    // Trigger autocomplete on "/"
    if (e.key === "/") {
      e.preventDefault();
      const ta = textareaRef.current;
      if (ta) {
        setShowAutocomplete(true);
        setAutocompleteFilter("");
        setSelectedIndex(0);
        // Position autocomplete near cursor
        const lines = ta.value.substring(0, ta.selectionStart).split("\n");
        const lineNum = lines.length;
        const charPos = lines[lines.length - 1].length;
        setAutocompletePos({
          top: Math.min(lineNum * 20 + 40, 200),
          left: Math.min(charPos * 8 + 16, 400),
        });
      }
    }

    // Tab inserts 4 spaces
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = textareaRef.current;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newVal = code.substring(0, start) + "    " + code.substring(end);
      onChange(newVal);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + 4; }, 0);
    }
  };

  const handleInput = (e) => {
    onChange(e.target.value);
    if (showAutocomplete) {
      // Update filter based on what's typed after /
      const ta = textareaRef.current;
      const textBefore = e.target.value.substring(0, ta.selectionStart);
      const lastSlash = textBefore.lastIndexOf("/");
      if (lastSlash === -1) {
        setShowAutocomplete(false);
      } else {
        const filter = textBefore.substring(lastSlash + 1);
        setAutocompleteFilter(filter);
        setSelectedIndex(0);
      }
    }
    // Clear validation when code changes
    if (validationState && validationState !== "validating") {
      setValidationState(null);
    }
  };

  const insertAttribute = (attr) => {
    const ta = textareaRef.current;
    const textBefore = code.substring(0, ta.selectionStart);
    const lastSlash = textBefore.lastIndexOf("/");
    const before = lastSlash >= 0 ? code.substring(0, lastSlash) : textBefore;
    const after = code.substring(ta.selectionStart);
    const insertion = `context["${attr.key}"]`;
    const newCode = before + insertion + after;
    onChange(newCode);
    setShowAutocomplete(false);
    setTimeout(() => {
      const newPos = before.length + insertion.length;
      ta.selectionStart = ta.selectionEnd = newPos;
      ta.focus();
    }, 0);
  };

  // Close autocomplete on click outside
  useEffect(() => {
    const handler = (e) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target)) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-medium text-foreground">
            Evaluation Code
          </label>
          <p className="text-muted-foreground text-sm mt-0.5">
            Write Python code to evaluate the call. Press <kbd className="px-1.5 py-0.5 bg-muted border border-border rounded text-xs font-mono">/</kbd> to browse available context attributes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative" ref={examplesRef}>
            <button
              type="button"
              onClick={() => setShowExamples(!showExamples)}
              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
            >
              <BookOpen className="h-3.5 w-3.5" />
              Examples
              <ChevronDown className={`h-3 w-3 transition-transform ${showExamples ? "rotate-180" : ""}`} />
            </button>
            {showExamples && (
              <div className="absolute right-0 mt-1 w-72 bg-card shadow-lg rounded-lg border border-border z-20 max-h-80 overflow-y-auto">
                {CODE_EXAMPLES.map((ex, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => { onChange(ex.code); setShowExamples(false); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-muted/50 border-b border-border last:border-0"
                  >
                    <div className="text-sm font-medium text-foreground">{ex.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{ex.desc}</div>
                    <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-muted text-muted-foreground">{ex.resultType}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowReference(!showReference)}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800"
          >
            <Info className="h-3.5 w-3.5" />
            {showReference ? "Hide" : "Show"} Reference
          </button>
        </div>
      </div>

      {/* Context reference panel */}
      {showReference && (
        <div className="bg-muted/50 border border-border rounded-lg p-4 text-xs space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold text-foreground">Context Attributes</span>
            <span className="text-muted-foreground">Access via <code className="bg-card px-1 py-0.5 rounded border">context["key"]</code> or <code className="bg-card px-1 py-0.5 rounded border">context.get("key", default)</code></span>
          </div>
          <div className="grid grid-cols-1 gap-1">
            {CONTEXT_ATTRIBUTES.map((attr) => (
              <div key={attr.key} className="flex items-start gap-2 py-0.5">
                <code className="bg-card border border-border px-1.5 py-0.5 rounded text-indigo-600 font-medium shrink-0">
                  {attr.key}
                </code>
                <span className="text-muted-foreground">{attr.desc}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-border pt-2 mt-2">
            <p className="text-muted-foreground font-medium mb-1">Required — <code className="bg-card border px-1 py-0.5 rounded text-green-700">metric</code> dict:</p>
            <div className="space-y-1">
              <div className="flex items-start gap-2">
                <code className="bg-card border px-1.5 py-0.5 rounded text-green-700 shrink-0">metric["result"]</code>
                <span className="text-muted-foreground">metric value (True/False, 1-5, enum string, number)</span>
              </div>
              <div className="flex items-start gap-2">
                <code className="bg-card border px-1.5 py-0.5 rounded text-green-700 shrink-0">metric["explanation"]</code>
                <span className="text-muted-foreground">string explaining the evaluation</span>
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-2 mt-2">
            <p className="text-muted-foreground font-medium mb-1">Optional — <code className="bg-card border px-1 py-0.5 rounded text-blue-700">structured_output</code> dict:</p>
            <div className="space-y-1">
              <div className="flex items-start gap-2">
                <code className="bg-card border px-1.5 py-0.5 rounded text-blue-700 shrink-0">structured_output["name"]</code>
                <span className="text-muted-foreground">field name — e.g. <code className="text-muted-foreground">"silence_compliance"</code></span>
              </div>
              <div className="flex items-start gap-2">
                <code className="bg-card border px-1.5 py-0.5 rounded text-blue-700 shrink-0">structured_output["value"]</code>
                <span className="text-muted-foreground">field value — e.g. <code className="text-muted-foreground">"pass"</code>, <code className="text-muted-foreground">"fail"</code></span>
              </div>
              <div className="flex items-start gap-2">
                <code className="bg-card border px-1.5 py-0.5 rounded text-blue-700 shrink-0">structured_output["classification"]</code>
                <span className="text-muted-foreground">one of: <code className="text-muted-foreground">meets_expectations</code>, <code className="text-muted-foreground">exceeds_expectations</code>, <code className="text-muted-foreground">requires_attention</code>, <code className="text-muted-foreground">goal_achieved</code>, <code className="text-muted-foreground">goal_missed</code></span>
              </div>
            </div>
          </div>
          <div className="border-t border-border pt-2 mt-2">
            <p className="text-muted-foreground">Use <code className="bg-card border px-1 py-0.5 rounded">context.get("key", default)</code> for safe access when data may not be available.</p>
          </div>
        </div>
      )}

      {/* Code editor */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={code}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          rows={14}
          spellCheck={false}
          className="block w-full rounded-lg border border-gray-700 shadow-sm font-mono text-sm bg-[#1e1e2e] text-[#cdd6f4] p-4 leading-6 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
          placeholder={PLACEHOLDER_CODE}
        />

        {/* Autocomplete dropdown */}
        {showAutocomplete && filteredAttrs.length > 0 && (
          <div
            ref={autocompleteRef}
            className="absolute z-20 bg-card border border-border rounded-lg shadow-lg max-h-56 overflow-y-auto w-80"
            style={{ top: autocompletePos.top, left: autocompletePos.left }}
          >
            {filteredAttrs.map((attr, i) => (
              <button
                key={attr.key}
                type="button"
                onClick={() => insertAttribute(attr)}
                className={`w-full text-left px-3 py-1.5 flex items-center gap-2 text-xs ${
                  i === selectedIndex ? "bg-indigo-50 text-indigo-700" : "text-foreground hover:bg-muted/50"
                }`}
              >
                <code className="font-mono font-medium text-indigo-600 shrink-0">{attr.key}</code>
                <span className="text-muted-foreground truncate">{attr.desc}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Validation bar */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleValidate}
          disabled={!code?.trim() || validationState === "validating"}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50 text-muted-foreground"
        >
          {validationState === "validating" ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5" />
          )}
          Validate Code
        </button>

        {/* Test with Call button */}
        <div className="relative" ref={callPickerRef}>
          <button
            type="button"
            onClick={handleOpenCallPicker}
            disabled={!code?.trim() || testState === "testing" || testState === "loading_calls"}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted/50 disabled:opacity-50 text-muted-foreground"
          >
            {testState === "testing" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : testState === "loading_calls" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            Test with Call
            <ChevronDown className="h-3 w-3" />
          </button>

          {showCallPicker && (
            <div className="absolute left-0 mt-1 w-80 bg-card shadow-lg rounded-lg border border-border z-20">
              <div className="p-2 border-b border-border">
                <input
                  type="text"
                  placeholder="Search by call identifier..."
                  value={callSearch}
                  onChange={(e) => setCallSearch(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  autoFocus
                />
              </div>
              <div className="max-h-52 overflow-y-auto">
                {(() => {
                  const filtered = testCalls.filter((c) =>
                    !callSearch.trim() || (c.call_identifier || `Call #${c.id}`).toLowerCase().includes(callSearch.toLowerCase())
                  );
                  return filtered.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-muted-foreground">
                      {testCalls.length === 0 ? "No completed calls for this agent" : "No matches"}
                    </div>
                  ) : (
                    filtered.map((call) => (
                      <button
                        key={call.id}
                        type="button"
                        onClick={() => { handleTestWithCall(call.id); setCallSearch(""); }}
                        className="w-full text-left px-4 py-2.5 hover:bg-muted/50 border-b border-border last:border-0"
                      >
                        <div className="text-sm font-medium text-foreground truncate">
                          {call.call_identifier || `Call #${call.id}`}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {call.call_duration ? `${Math.round(call.call_duration)}s` : ""}{call.call_direction ? ` · ${call.call_direction}` : ""}{call.agent?.name ? ` · ${call.agent.name}` : ""}
                        </div>
                      </button>
                    ))
                  );
                })()}
              </div>
            </div>
          )}
        </div>

        {validationState && validationState !== "validating" && (
          <div className={`flex items-center gap-1.5 text-sm ${validationState.valid ? "text-green-600" : "text-red-600"}`}>
            {validationState.valid ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                <span>Code is valid</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                <span>{validationState.errors?.[0] || "Invalid code"}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Test result preview */}
      {testState && testState !== "testing" && testState !== "loading_calls" && (
        <div className={`rounded-lg border p-4 text-sm ${testState.error ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-foreground">Test Result</span>
            <button type="button" onClick={() => setTestState(null)} className="text-muted-foreground hover:text-muted-foreground">
              <XCircle className="h-4 w-4" />
            </button>
          </div>
          {testState.error ? (
            <div className="text-red-700">{testState.error}</div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs font-medium text-muted-foreground uppercase">Result</span>
                  <div className="font-mono text-foreground mt-0.5">{String(testState.result)}</div>
                </div>
                <div>
                  <span className="text-xs font-medium text-muted-foreground uppercase">Execution Time</span>
                  <div className="text-foreground mt-0.5">{testState.execution_time_ms}ms</div>
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-muted-foreground uppercase">Explanation</span>
                <div className="text-foreground mt-0.5">{testState.explanation || "—"}</div>
              </div>
              {testState.structured_output && (
                <div className="border-t border-green-200 pt-2 mt-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase">Structured Output</span>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <div>
                      <span className="text-xs text-muted-foreground">Name</span>
                      <div className="font-mono text-foreground">{testState.structured_output.set_metrics_name}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Value</span>
                      <div className="font-mono text-foreground">{testState.structured_output.as_metrics_expected_value}</div>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Classification</span>
                      <div className="font-mono text-foreground">{testState.structured_output.outcome_classification}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CodeMetricEditor;
